import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { Admin as AdminCmp } from '../../../components/Admin';
import { RightDrawer } from '../../../components/RightDrawer';
import { SpeedDial } from '../../../components/SpeedDial';
import { GenericAdminForm } from '../../../forms/GenericAdmin';
import type { GenericAdminFormValues } from '../../../forms/GenericAdmin';

import { useFoapuUserMutation } from './hooks/useFoapuUserMutation.hook';
import { useInvalidateUserQueries } from './hooks/useInvalidateUserQueries.hook';

export const UserPage_Admin_User_Fragment = graphql(`
  fragment UserPage_Admin_User_Fragment on User {
    _id
    createdAt
    creatorId
    statusId
    updatedAt
    updaterId
  }
`);

interface Props {
  readonly user: FragmentType<typeof UserPage_Admin_User_Fragment>;
  // Without edit path (own account), the administration is read-only.
  readonly paths: Readonly<{ consult: string; edit?: string }>;
}

export const Admin = ({ user: pUser, paths }: Readonly<Props>) => {
  const user = getFragmentData(UserPage_Admin_User_Fragment, pUser);

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const isInEditionMode = !!paths.edit && currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { mutate: foapuUserMutation } = useFoapuUserMutation();
  const invalidateUserQueries = useInvalidateUserQueries();

  const mutate = useCallback(
    (
      values: GenericAdminFormValues,
      handleError: (error: unknown) => unknown,
      handleSuccess: () => unknown,
    ) => {
      foapuUserMutation(
        { filter: { _id: user._id }, update: values },
        {
          onError: handleError,
          onSuccess: () => {
            leaveEditionMode();
            enqueueSnackbar('Modification enregistrée.');
            handleSuccess();
            invalidateUserQueries(user._id);
          },
        },
      );
    },
    [
      enqueueSnackbar,
      foapuUserMutation,
      invalidateUserQueries,
      leaveEditionMode,
      user._id,
    ],
  );

  return (
    <>
      <AdminCmp
        createdAt={user.createdAt}
        creatorId={user.creatorId}
        id={user._id}
        statusId={user.statusId}
        updatedAt={user.updatedAt}
        updaterId={user.updaterId}
      />
      {!!paths.edit && (
        <>
          <SpeedDial
            actions={[
              {
                id: 'userAdmin',
                icon: <DisplaySettingsIcon />,
                name: 'Administrer',
                to: paths.edit,
              },
            ]}
            title="Actions possibles sur l'Administration de l'Utilisateur"
          />
          <RightDrawer
            isOpen={isInEditionMode}
            onDrawerClose={leaveEditionMode}
          >
            <GenericAdminForm
              askMemoBeforeSubmit={false}
              onCancel={leaveEditionMode}
              onSubmit={mutate}
              statusId={user.statusId as GenericStatusId}
            />
          </RightDrawer>
        </>
      )}
    </>
  );
};
